package er.prateek.crossly.progression

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import er.prateek.crossly.config.StreakConfig
import er.prateek.crossly.data.model.PlayStreak
import er.prateek.crossly.persistence.ProgressStore
import kotlin.time.Clock
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.todayIn

/** What one completed game did to the streak -- drives analytics and the achievement check. */
data class StreakUpdate(
    val before: Int,
    val after: Int,
    /** True only for the first completion of a day that grew the streak. */
    val extended: Boolean,
    /** The (unrestored) streak this play replaced, if the player came back after a break. */
    val lostStreak: Int?,
)

/** A lost streak that can still be bought back, and what it currently costs. */
data class StreakRestoreOffer(
    val lostStreak: Int,
    val adsRequired: Int,
    val adsWatched: Int,
    /** Inside the cheap first window (one ad by default). */
    val withinGrace: Boolean,
    val expiresAt: Instant,
    /** Star price for each *remaining* ad when a rewarded ad can't be shown; `0` = not offered. */
    val starCostPerAd: Int,
) {
    val adsRemaining: Int get() = (adsRequired - adsWatched).coerceAtLeast(0)
    val starCostRemaining: Int get() = adsRemaining * starCostPerAd
}

/** One cell of the home screen's week strip. */
data class StreakDay(val date: LocalDate, val played: Boolean, val restored: Boolean, val isToday: Boolean)

/**
 * The any-game daily streak (see [PlayStreak]). Compose-observable: read [streak] / [displayStreak]
 * from a composable and it recomposes on every change.
 *
 * Days are the player's local calendar days. A streak is *alive* while the last played day is
 * today or yesterday; after that it is *broken* but its count is kept, so [restoreOffer] can sell
 * it back within [StreakConfig.restoreWindowHours] of the break.
 */
class StreakManager(
    private val store: ProgressStore,
    private val config: () -> StreakConfig = { StreakConfig() },
    private val clock: Clock = Clock.System,
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() },
) {
    var streak: PlayStreak by mutableStateOf(loadOrSeed())
        private set

    private fun loadOrSeed(): PlayStreak {
        val progress = store.load()
        progress.playStreak?.let { return it }
        // First launch on a build with the any-game streak: carry the daily-puzzle streak over.
        // Its dates are UTC, which is at most a day off and only matters for this one migration.
        val legacy = progress.dailyStreak
        val seeded = PlayStreak(
            current = legacy.currentStreak,
            longest = legacy.longestStreak,
            lastPlayedDate = legacy.lastCompletedDate,
            playedDates = listOfNotNull(legacy.lastCompletedDate),
        )
        store.save(progress.copy(playStreak = seeded))
        return seeded
    }

    fun today(): LocalDate = clock.todayIn(timeZone())

    private fun lastPlayed(): LocalDate? = streak.lastPlayedDate?.let { runCatching { LocalDate.parse(it) }.getOrNull() }

    private fun daysSinceLastPlayed(): Long? = lastPlayed()?.let { today().toEpochDays().toLong() - it.toEpochDays().toLong() }

    fun playedToday(): Boolean = daysSinceLastPlayed() == 0L

    /** Alive = played today, or played yesterday and still has today to keep it going. */
    fun isAlive(): Boolean = daysSinceLastPlayed()?.let { it <= 1 } ?: false

    /** The number to show the player: the stored count while alive, otherwise 0. */
    fun displayStreak(): Int = if (isAlive()) streak.current else 0

    /** Alive, but today isn't played yet -- the "don't lose it" state notifications key off. */
    fun isAtRisk(): Boolean = streak.current > 0 && daysSinceLastPlayed() == 1L

    /** The local midnight at which the stored streak was lost, or null while it's alive. */
    private fun brokeAt(): Instant? {
        val last = lastPlayed() ?: return null
        if (isAlive() || streak.current <= 0) return null
        return LocalDate.fromEpochDays(last.toEpochDays() + 2).atStartOfDayIn(timeZone())
    }

    fun restoreOffer(): StreakRestoreOffer? {
        val cfg = config()
        if (!cfg.restoreEnabled || streak.current < cfg.minStreakToRestore) return null
        val brokeAt = brokeAt() ?: return null
        val since = clock.now() - brokeAt
        if (since > cfg.restoreWindowHours.hours) return null
        val withinGrace = since <= cfg.graceHours.hours
        val required = (if (withinGrace) cfg.adsWithinGrace else cfg.adsAfterGrace).coerceAtLeast(1)
        val watched = if (streak.restoreProgressFor == streak.lastPlayedDate) streak.restoreAdsWatched else 0
        return StreakRestoreOffer(
            lostStreak = streak.current,
            adsRequired = required,
            adsWatched = watched.coerceAtMost(required),
            withinGrace = withinGrace,
            expiresAt = brokeAt + cfg.restoreWindowHours.hours,
            starCostPerAd = cfg.starCostPerAd.coerceAtLeast(0),
        )
    }

    /** Whether Home should auto-open the restore sheet: an offer exists and it hasn't opened for this break yet. */
    fun shouldAutoPromptRestore(): Boolean =
        restoreOffer() != null && streak.restorePromptedFor != streak.lastPlayedDate

    fun markRestorePrompted() {
        save(streak.copy(restorePromptedFor = streak.lastPlayedDate))
    }

    /**
     * Records one earned rewarded ad toward the current offer. Returns true when that ad completed
     * the restore. Progress is persisted, so watching one of two ads and leaving isn't wasted.
     */
    fun recordRestoreAd(): Boolean {
        val offer = restoreOffer() ?: return false
        val watched = offer.adsWatched + 1
        if (watched >= offer.adsRequired) {
            restore()
            return true
        }
        save(streak.copy(restoreAdsWatched = watched, restoreProgressFor = streak.lastPlayedDate))
        return false
    }

    /**
     * Bridges the missed days so the streak is alive again (last played = yesterday). The bridged
     * days don't add to the count -- they're drawn as frozen -- and today still has to be played.
     */
    fun restore(): Boolean {
        val last = lastPlayed() ?: return false
        if (restoreOffer() == null) return false
        val yesterday = today().toEpochDays() - 1
        val bridged = (last.toEpochDays() + 1..yesterday).map { LocalDate.fromEpochDays(it).toString() }
        save(
            streak.copy(
                lastPlayedDate = LocalDate.fromEpochDays(yesterday).toString(),
                restoredDates = (streak.restoredDates + bridged).takeLast(HISTORY_DAYS),
                restoresUsed = streak.restoresUsed + 1,
                restoreAdsWatched = 0,
                restoreProgressFor = null,
            ),
        )
        return true
    }

    /** Call on every completed game, in any mode. Idempotent within a day. */
    fun recordPlay(): StreakUpdate {
        val before = displayStreak()
        val todayIso = today().toString()
        if (streak.lastPlayedDate == todayIso) return StreakUpdate(before, before, extended = false, lostStreak = null)

        val alive = isAlive()
        val lost = if (!alive && streak.current > 0) streak.current else null
        val next = if (alive) streak.current + 1 else 1
        save(
            streak.copy(
                current = next,
                longest = maxOf(streak.longest, next),
                lastPlayedDate = todayIso,
                playedDates = (streak.playedDates + todayIso).distinct().takeLast(HISTORY_DAYS),
                restoreAdsWatched = 0,
                restoreProgressFor = null,
            ),
        )
        return StreakUpdate(before = before, after = next, extended = true, lostStreak = lost)
    }

    /** The last [days] days ending today, oldest first. */
    fun recentDays(days: Int = 7): List<StreakDay> {
        val today = today()
        val played = streak.playedDates.toSet()
        val restored = streak.restoredDates.toSet()
        return (days - 1 downTo 0).map { back ->
            val date = LocalDate.fromEpochDays(today.toEpochDays() - back)
            val iso = date.toString()
            StreakDay(date, played = iso in played, restored = iso in restored, isToday = back == 0)
        }
    }

    fun reset() {
        save(PlayStreak())
    }

    private fun save(updated: PlayStreak) {
        streak = updated
        store.save(store.load().copy(playStreak = updated))
    }

    private companion object {
        const val HISTORY_DAYS = 60
    }
}
